import React, { useState } from "react";
import {
  Box,
  Flex,
  IconButton,
  VStack,
  Heading,
  SimpleGrid,
} from "@chakra-ui/react";
import { IoArrowBackCircleOutline } from "react-icons/io5";
import FormInput from "../../../components/FormInput";
import PrimaryButton from "../../../components/PrimaryButton";
import PrimaryHeading from "../../../components/PrimaryHeading";

const TemplateForm = ({
  shareOpinionFieldsData,
  onBack,
  onSubmit,
  selectedMType,
  isVilTemplate = false,
}) => {
  const [formData, setFormData] = useState({});
  const [validationErrors, setValidationErrors] = useState({});

  // Fields to hide in Appointment Letter when selectedMType is "ALL"
  const duplicateFields = [
    "patient_name",
    "requested_doctor",
    "requested_doctor_speciality",
  ];

  // Group fields by data_field_type and sort by serial_no
  const groupedFields = shareOpinionFieldsData.reduce((acc, field) => {
    // Skip attendant fields as they'll be handled separately
    if (field.field_key === "attendant_name") {
      return acc;
    }

    // For VIL template, use a default section if data_field_type is not present
    const sectionType = isVilTemplate ? "VIL Details" : field.data_field_type;

    if (!acc[sectionType]) {
      acc[sectionType] = [];
    }
    acc[sectionType].push(field);
    return acc;
  }, {});

  // Get attendants data if available
  const attendantsData =
    shareOpinionFieldsData.find((field) => field.field_key === "attendant_name")
      ?.field_value || [];

  // Create separate section for attendants
  if (isVilTemplate && attendantsData.length > 0) {
    groupedFields["Attendants"] = attendantsData.map((attendant, index) => ({
      field_key: `attendant_${index + 1}`,
      label: `Attendant ${index + 1}`,
      is_editable: true,
      attendant_details: attendant,
    }));
  }

  // Update section order to include Attendants section
  const sectionOrder = isVilTemplate
    ? ["VIL Details", "Attendants"]
    : [
        "Medical Opinion",
        "Bank Details",
        "Wire Transfer Bank Details",
        "Appointment Letter",
      ];

  // Sort fields within each group by serial_no
  Object.keys(groupedFields).forEach((key) => {
    groupedFields[key].sort((a, b) => a.serial_no - b.serial_no);
  });

  const validateField = (fieldKey, value, fieldType) => {
    // Clear any existing error for this field
    if (validationErrors[fieldKey]) {
      setValidationErrors(prev => {
        const newErrors = {...prev};
        delete newErrors[fieldKey];
        return newErrors;
      });
    }

    // Log all date validation attempts for debugging
    if (fieldKey === "appointment_date" ) {
      console.log(`Validating date field: ${fieldKey}, value: ${value}, type: ${fieldType}`);
      
      // Check specifically for appointment date fields - be more explicit here
      if (fieldKey === "appointment_date" || fieldKey.includes("_date")) {
        if (!value) return true; // Skip validation if empty
        
        try {
          // Convert to date object - handle various formats
          let selectedDate;
          
          // Handle ISO string format (YYYY-MM-DD)
          if (typeof value === 'string' && value.includes('-')) {
            selectedDate = new Date(value);
          } else {
            // Try to handle other possible formats
            selectedDate = new Date(value);
          }
          
          const today = new Date();
          
          // Set both dates to midnight for fair comparison
          selectedDate.setHours(0, 0, 0, 0);
          today.setHours(0, 0, 0, 0);
          
          console.log(`COMPARING DATES - Field: ${fieldKey}`);
          console.log(`  Selected date: ${selectedDate.toDateString()} (${selectedDate.getTime()})`);
          console.log(`  Today's date: ${today.toDateString()} (${today.getTime()})`);
          console.log(`  Is past date? ${selectedDate < today}`);
          
          if (selectedDate < today) {
            console.log(`✖️ VALIDATION FAILED: ${fieldKey} has past date`);
            setValidationErrors(prev => ({
              ...prev,
              [fieldKey]: "Date cannot be in the past"
            }));
            return false;
          } else {
            console.log(`✓ VALIDATION PASSED: ${fieldKey} has current/future date`);
          }
        } catch (err) {
          console.error(`Error validating date for ${fieldKey}:`, err);
        }
      }
    }
    
    // Time validation for appointment time fields
    if (fieldKey === "appointment_from_time" || fieldKey === "appointment_to_time") {
      
      console.log(`Validating time field: ${fieldKey}, value: ${value}`);
      
      const fromTime = fieldKey === "appointment_from_time" 
        ? value 
        : formData["appointment_from_time"];
        
      const toTime = fieldKey === "appointment_to_time" 
        ? value 
        : formData["appointment_to_time"];
      
      // Only validate if both times are available
      if (fromTime && toTime) {
        console.log(`Comparing times - From: ${fromTime}, To: ${toTime}`);
        
        // Convert to Date objects for comparison (using today's date)
        const today = new Date().toISOString().split('T')[0];
        const fromDateTime = new Date(`${today}T${fromTime}`);
        const toDateTime = new Date(`${today}T${toTime}`);
        
        console.log(`Converted times - From: ${fromDateTime}, To: ${toDateTime}`);
        
        if (fromDateTime >= toDateTime) {
          const errorMsg = "Start time must be earlier than end time";
          console.log(`❌ VALIDATION FAILED: ${errorMsg}`);
          
          // Set error on the current field being changed
          setValidationErrors(prev => ({
            ...prev,
            [fieldKey]: errorMsg
          }));
          return false;
        } else {
          // Clear any existing errors on both time fields
          setValidationErrors(prev => {
            const newErrors = {...prev};
            delete newErrors["appointment_from_time"];
            delete newErrors["appointment_to_time"];
            return newErrors;
          });
          console.log(`✓ VALIDATION PASSED: Times are valid`);
        }
      }
    }
    
    return true;
  };

  const handleInputChange = (fieldKey, value, attendantIndex, fieldType) => {
    console.log(`Input changed: ${fieldKey} = ${value} (type: ${fieldType || 'unknown'})`);
    
    // Identify the actual field type for validation
    let actualFieldType;
    if (!fieldType) {
      // Look up field type from the original data for regular fields
      const field = shareOpinionFieldsData.find(f => f.field_key === fieldKey);
      actualFieldType = field?.field_type;
      console.log(`Field ${fieldKey} determined to have type: ${actualFieldType}`);
    } else {
      actualFieldType = fieldType;
    }

    // Validate the field
    const isValid = validateField(fieldKey, value, actualFieldType);
    console.log(`Validation result for ${fieldKey}: ${isValid ? 'valid' : 'invalid'}`);

    // Update form data regardless of validation
    if (attendantIndex !== undefined) {
      // Handle attendant field changes
      setFormData((prev) => ({
        ...prev,
        [`attendant_${attendantIndex}_${fieldType}`]: value,
      }));
    } else {
      // Handle regular field changes
      setFormData((prev) => ({
        ...prev,
        [fieldKey]: value,
      }));
    }
  };

  const handleSubmit = () => {
    console.log("🔍 FORM SUBMISSION: Checking all fields for validation");
    
    // Print out all form data to check values
    console.log("Current form data:", formData);
    
    // Validate all fields before submission
    let hasErrors = false;
    const newErrors = {...validationErrors};
    
    // Check date fields in all sections
    Object.keys(groupedFields).forEach(section => {
      console.log(`Checking fields in section: ${section}`);
      
      // Log all fields in this section for debugging
      console.log(`Fields in ${section}:`, 
        groupedFields[section].map(f => ({ key: f.field_key, type: f.field_type }))
      );
      
      groupedFields[section].forEach(field => {
        const fieldType = field.field_type;
        const fieldKey = field.field_key;
        
        // More explicit check for appointment date fields
        if ((fieldType === "date" || fieldType === "datetime-local" || fieldType === "datetime") &&
            (fieldKey === "appointment_date" || fieldKey.includes("_date"))) {
          
          // Get the current value, either from form data or original data
          const value = formData[fieldKey] !== undefined ? formData[fieldKey] : field.field_value;
          
          console.log(`📅 Found date field: ${fieldKey} = ${value}`);
          
          if (value) {
            try {
              const selectedDate = new Date(value);
              const today = new Date();
              
              // Check if date is valid
              if (isNaN(selectedDate.getTime())) {
                console.error(`Invalid date format: ${value}`);
                return; // Skip invalid dates
              }
              
              // Reset hours for date comparison
              selectedDate.setHours(0, 0, 0, 0);
              today.setHours(0, 0, 0, 0);
              
              console.log(`Comparing: ${selectedDate.toDateString()} vs ${today.toDateString()}`);
              
              if (selectedDate < today) {
                console.error(`⚠️ VALIDATION ERROR: ${fieldKey} has past date`);
                newErrors[fieldKey] = "Date cannot be in the past";
                hasErrors = true;
              }
            } catch (err) {
              console.error(`Error processing date for ${fieldKey}:`, err);
            }
          }
        }
      });
    });

    // Validate appointment times
    const fromTime = formData["appointment_from_time"];
    const toTime = formData["appointment_to_time"];
    
    if (fromTime && toTime) {
      console.log(`Validating appointment times - From: ${fromTime}, To: ${toTime}`);
      
      // Convert to Date objects for comparison (using today's date)
      const today = new Date().toISOString().split('T')[0];
      const fromDateTime = new Date(`${today}T${fromTime}`);
      const toDateTime = new Date(`${today}T${toTime}`);
      
      if (fromDateTime >= toDateTime) {
        console.error(`⚠️ VALIDATION ERROR: Appointment start time must be earlier than end time`);
        newErrors["appointment_from_time"] = "Start time must be earlier than end time";
        newErrors["appointment_to_time"] = "End time must be later than start time";
        hasErrors = true;
      }
    }

    // Update validation errors
    if (hasErrors) {
      console.error('Form has validation errors:', newErrors);
      setValidationErrors(newErrors);
      return; // Prevent submission if there are errors
    }

    console.log("All validations passed, proceeding with submission");
    let payload = {};

    if (isVilTemplate) {
      payload.vil_det = {};

      // Add all regular fields from VIL Details section
      groupedFields["VIL Details"].forEach((field) => {
        payload.vil_det[field.field_key] =
          formData[field.field_key] || field.field_value || "N/A";
      });

      // Get original attendants data
      const attendantsData =
        shareOpinionFieldsData.find(
          (field) => field.field_key === "attendant_name"
        )?.field_value || [];

      // Map attendants with updated values
      payload.vil_det.attendants = attendantsData.map((attendant, index) => ({
        attendant_name:
          formData[`attendant_${index}_name`] ||
          attendant.attendant_name ||
          "N/A",
        attendant_passport:
          formData[`attendant_${index}_passport`] ||
          attendant.attendant_passport ||
          "N/A",
        attendant_nationality:
          formData[`attendant_${index}_nationality`] ||
          attendant.attendant_nationality ||
          "N/A",
        attendant_relation:
          formData[`attendant_${index}_relation`] ||
          attendant.attendant_relation ||
          "N/A",
      }));
    } else {
      // Existing logic for Medical Opinion and Appointment Letter
      if (selectedMType === "MO" || selectedMType === "ALL") {
        payload.medical_opinion = {};
        [
          "Medical Opinion",
          "Bank Details",
          "Wire Transfer Bank Details",
        ].forEach((section) => {
          if (groupedFields[section]) {
            groupedFields[section].forEach((field) => {
              payload.medical_opinion[field.field_key] =
                formData[field.field_key] || field.field_value || "N/A";
            });
          }
        });
      }
      if (selectedMType === "AL" || selectedMType === "ALL") {
        payload.appointment_letter = {};
        if (groupedFields["Appointment Letter"]) {
          groupedFields["Appointment Letter"].forEach((field) => {
            if (
              selectedMType === "ALL" &&
              duplicateFields.includes(field.field_key)
            ) {
              const moField = groupedFields["Medical Opinion"]?.find(
                (f) => f.field_key === field.field_key
              );
              payload.appointment_letter[field.field_key] = moField
                ? formData[moField.field_key] || moField.field_value || "N/A"
                : formData[field.field_key] || field.field_value || "N/A";
            } else {
              payload.appointment_letter[field.field_key] =
                formData[field.field_key] || field.field_value || "N/A";
            }
          });
        }
      }
    }

    onSubmit(payload);
  };

  const shouldShowField = (section, field) => {
    if (section !== "Appointment Letter") return true;
    if (selectedMType !== "ALL") return true;
    return !duplicateFields.includes(field.field_key);
  };
  const stripHtmlTags = (html) => {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;
    return tempDiv.textContent || tempDiv.innerText || "";
  };

  return (
    <Box p={4}>
      {/* Header with Back Button */}
      <Box bg="green.300" color="white" p={2} borderTopRadius="md" mb={4}>
        <Flex justify="space-between" align="center">
          <Heading size="lg" color="white">
            {isVilTemplate
              ? "VIL Template"
              : selectedMType === "MO"
              ? "Medical Opinion"
              : selectedMType === "AL"
              ? "Appointment"
              : "Medical Opinion & Appointment"}
          </Heading>
          <IconButton
            color="primary.1"
            size="lg"
            aria-label="Back"
            fontSize="30px"
            isRound={true}
            variant="solid"
            icon={<IoArrowBackCircleOutline />}
            onClick={onBack}
          />
        </Flex>
      </Box>

      {/* Form Sections */}
      <VStack spacing={8} align="stretch">
        {sectionOrder.map((sectionType) =>
          groupedFields[sectionType] ? (
            <Box
              key={sectionType}
              bg="white"
              p={6}
              borderRadius="md"
              shadow="sm"
            >
              <PrimaryHeading size="md" mb={4} color="primary.1">
                {sectionType}
              </PrimaryHeading>
              <VStack spacing={4} align="stretch">
                <SimpleGrid columns={2} spacing={4}>
                  {sectionType === "Attendants"
                    ? // Render attendants fields
                      groupedFields[sectionType].map((field, index) => (
                        <Box
                          key={field.field_key}
                          p={4}
                          borderWidth="1px"
                          borderRadius="md"
                        >
                          <PrimaryHeading size="sm" mb={2}>
                            {field.label}
                          </PrimaryHeading>
                          {field.attendant_details.attendant_name && (
                            <FormInput
                              label="Name"
                              value={
                                formData[`attendant_${index}_name`] ||
                                field.attendant_details.attendant_name
                              }
                              onChange={(e) =>
                                handleInputChange(
                                  field.field_key,
                                  e.target.value,
                                  index,
                                  "name"
                                )
                              }
                              isReadOnly={!field.is_editable}
                            />
                          )}
                          {field.attendant_details.attendant_passport && (
                            <FormInput
                              label="Passport"
                              value={
                                formData[`attendant_${index}_passport`] ||
                                field.attendant_details.attendant_passport
                              }
                              onChange={(e) =>
                                handleInputChange(
                                  field.field_key,
                                  e.target.value,
                                  index,
                                  "passport"
                                )
                              }
                              isReadOnly={!field.is_editable}
                            />
                          )}
                          {field.attendant_details.attendant_nationality && (
                            <FormInput
                              label="Nationality"
                              value={
                                formData[`attendant_${index}_nationality`] ||
                                field.attendant_details.attendant_nationality
                              }
                              onChange={(e) =>
                                handleInputChange(
                                  field.field_key,
                                  e.target.value,
                                  index,
                                  "nationality"
                                )
                              }
                              isReadOnly={!field.is_editable}
                            />
                          )}
                          {field.attendant_details.attendant_relation !==
                            undefined && (
                            <FormInput
                              label="Relation"
                              value={
                                formData[`attendant_${index}_relation`] ||
                                field.attendant_details.attendant_relation ||
                                ""
                              }
                              onChange={(e) =>
                                handleInputChange(
                                  field.field_key,
                                  e.target.value,
                                  index,
                                  "relation"
                                )
                              }
                              isReadOnly={!field.is_editable}
                            />
                          )}
                        </Box>
                      ))
                    : // Render regular fields
                      groupedFields[sectionType]
                        .filter((field) => shouldShowField(sectionType, field))
                        .map((field) => (
                          <FormInput
                            key={field.field_key}
                            id={field.field_key}
                            label={field.label}
                            type={field.field_type}
                            value={
                              formData[field.field_key] ||
                              stripHtmlTags(field.field_value)
                            }
                            onChange={(e) =>
                              handleInputChange(field.field_key, e.target.value)
                            }
                            placeholder={field.placeholder}
                            isReadOnly={!field.is_editable}
                            isInvalid={!!validationErrors[field.field_key]}
                            errorMessage={validationErrors[field.field_key]}
                          />
                        ))}
                </SimpleGrid>
              </VStack>
            </Box>
          ) : null
        )}
      </VStack>

      {/* Submit Button */}
      <Flex justify="end" mt={8}>
        <PrimaryButton 
          onClick={handleSubmit} 
          width="300px"
          isDisabled={Object.keys(validationErrors).length > 0}
        >
          {isVilTemplate
            ? "Generate VIL"
            : selectedMType === "MO"
            ? "Generate Medical Opinion"
            : selectedMType === "AL"
            ? "Generate Appointment"
            : "Generate"}
        </PrimaryButton>
      </Flex>
    </Box>
  );
};

export default TemplateForm;
